import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { 
  MapPin, 
  Calendar, 
  Award, 
  Heart, 
  Users, 
  Target,
  Quote,
  ArrowRight,
  Star,
  Shield,
  Handshake
} from 'lucide-react';

const milestones = [
  {
    year: '2004',
    title: 'The Beginning',
    description: 'Ron Manthei starts collecting sports cards in Kansas City, turning a childhood passion into a serious pursuit.'
  },
  {
    year: '2010',
    title: 'Going Pro',
    description: 'After years of trading at local shows and online, Ron becomes a full-time dealer specializing in graded vintage cards.'
  },
  {
    year: '2018',
    title: 'The Problem',
    description: 'Frustrated by rising marketplace fees eating into profits, Ron begins dreaming of a better solution for sellers.'
  },
  {
    year: '2024',
    title: 'Slab Society Born',
    description: 'Ron launches Slab Society with a revolutionary 0% seller commission model, putting collectors first.'
  },
];

const values = [
  {
    icon: Heart,
    title: 'Collector-First',
    description: "We're collectors ourselves. Every decision we make starts with one question: Is this good for the hobby?"
  },
  {
    icon: Handshake,
    title: 'Fair Dealing',
    description: "No hidden fees, no surprise charges. What you see is what you get. Transparency isn't a feature—it's who we are."
  },
  {
    icon: Shield,
    title: 'Trust & Security',
    description: 'Every transaction is protected. Every seller is verified. Every card is authentic. No exceptions.'
  },
  {
    icon: Users,
    title: 'Community',
    description: "We're building more than a marketplace. We're building a society of passionate collectors who support each other."
  },
];

export const AboutUsPage = () => {
  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 spotlight">
        <div className="max-w-4xl mx-auto text-center">
          <span className="badge badge-amber mb-6">Our Story</span>
          
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase text-white leading-[0.9]">
            The Story of<br />
            <span className="text-blue-400">Slab Society</span>
          </h1>
          
          <p className="mt-8 text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Born in Kansas City. Built by collectors. For collectors.
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="stat-card p-8 md:p-12">
            <div className="grid md:grid-cols-5 gap-8 items-center">
              {/* Photo/Logo */}
              <div className="md:col-span-2 flex justify-center">
                <div className="relative">
                  <div className="w-48 h-48 md:w-64 md:h-64 rounded-2xl bg-gradient-to-br from-blue-600/20 to-amber-500/20 border border-white/10 flex items-center justify-center overflow-hidden">
                    <img 
                      src={process.env.REACT_APP_LOGO_URL} 
                      alt="Slab Society"
                      className="w-32 h-32 md:w-40 md:h-40 object-contain"
                    />
                  </div>
                  {/* Badge */}
                  <div className="absolute -bottom-3 -right-3 px-4 py-2 rounded-xl bg-amber-500 text-black font-bold text-sm">
                    Est. 2024
                  </div>
                </div>
              </div>
              
              {/* Bio */}
              <div className="md:col-span-3 space-y-6">
                <div>
                  <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase text-white">
                    Ron Manthei
                  </h2>
                  <p className="text-amber-500 font-semibold mt-1">
                    Founder & CEO
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <MapPin className="w-4 h-4 text-blue-400" />
                    <span>Kansas City, Missouri</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <span>20+ Years in the Hobby</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Award className="w-4 h-4 text-amber-400" />
                    <span>Verified Dealer</span>
                  </div>
                </div>
                
                <div className="relative">
                  <Quote className="absolute -top-2 -left-2 w-8 h-8 text-blue-500/30" />
                  <blockquote className="text-zinc-300 text-lg leading-relaxed pl-6">
                    "After two decades of buying and selling cards, I got tired of watching platforms 
                    take 13%, 15%, even 20% of every sale. That's money that belongs in collectors' 
                    pockets, not corporate coffers. So I built something different."
                  </blockquote>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Kansas City Story */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0C0C0E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-blue mb-4">Kansas City Roots</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight uppercase text-white">
              Where It All Started
            </h2>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <div className="stat-card p-8 space-y-6 text-zinc-300 leading-relaxed">
              <p className="text-lg">
                It started in a basement in <span className="text-white font-semibold">Kansas City</span>. 
                Stacks of cards, a price guide, and a dream. Ron Manthei spent his weekends 
                at card shows across the Midwest, building relationships and a reputation for fair dealing.
              </p>
              
              <p className="text-lg">
                Over <span className="text-white font-semibold">two decades</span>, Ron watched the hobby 
                transform. Cards that sold for dollars became worth thousands. The internet opened up a 
                global marketplace. But something else changed too—the platforms that connected buyers 
                and sellers started taking bigger and bigger cuts.
              </p>
              
              <p className="text-lg">
                "I remember selling a $500 card and seeing $65 disappear to fees," Ron recalls. 
                "That's when I knew something had to change. <span className="text-emerald-400 font-semibold">
                Collectors deserve better.</span>"
              </p>
              
              <p className="text-lg">
                Slab Society was born from that frustration—and from Ron's belief that a marketplace 
                could be profitable AND fair. The <span className="text-white font-semibold">0% seller 
                commission</span> model isn't just a marketing gimmick. It's a promise that the money 
                you earn stays where it belongs: with you.
              </p>
              
              <div className="pt-6 border-t border-white/10">
                <p className="text-zinc-500 italic">
                  "Kansas City taught me that handshake deals and your word still matter. 
                  That's the spirit we're bringing to the digital age."
                </p>
                <p className="text-zinc-400 mt-2">— Ron Manthei</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-amber mb-4">20 Years in the Making</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight uppercase text-white">
              Our Journey
            </h2>
          </div>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500 via-amber-500 to-emerald-500" />
            
            <div className="space-y-12">
              {milestones.map((milestone, i) => (
                <div 
                  key={milestone.year}
                  className={`relative flex items-center gap-8 ${
                    i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 -translate-x-1/2 rounded-full bg-blue-500 border-4 border-[#09090B] z-10" />
                  
                  {/* Content */}
                  <div className={`ml-16 md:ml-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                    <div className="stat-card p-6">
                      <span className="font-mono text-2xl font-bold text-blue-400">{milestone.year}</span>
                      <h3 className="font-heading font-bold text-xl text-white mt-2">{milestone.title}</h3>
                      <p className="text-zinc-400 mt-2">{milestone.description}</p>
                    </div>
                  </div>
                  
                  {/* Spacer for alternating layout */}
                  <div className="hidden md:block md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0C0C0E]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-emerald mb-4">What We Stand For</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight uppercase text-white">
              Our Values
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, i) => (
              <div key={value.title} className="stat-card p-6 hover:border-white/10 transition-colors group">
                <value.icon className="w-10 h-10 text-blue-400 mb-4 group-hover:text-amber-400 transition-colors" />
                <h3 className="font-heading font-bold text-xl text-white mb-2">{value.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="stat-card p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-amber-500/5" />
            
            <div className="relative z-10">
              <Target className="w-16 h-16 text-blue-400 mx-auto mb-8" />
              
              <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase text-white mb-6">
                Our Mission
              </h2>
              
              <p className="text-xl text-zinc-300 leading-relaxed max-w-2xl mx-auto">
                To build the most <span className="text-white font-semibold">trusted</span>, 
                <span className="text-white font-semibold"> fair</span>, and 
                <span className="text-white font-semibold"> collector-focused</span> marketplace 
                in the hobby—where sellers keep what they earn and buyers find what they love.
              </p>
              
              <div className="flex items-center justify-center gap-8 mt-12 pt-8 border-t border-white/10">
                <div className="text-center">
                  <p className="font-mono text-3xl font-bold text-emerald-400">0%</p>
                  <p className="text-zinc-500 text-sm">Seller Fees</p>
                </div>
                <div className="w-px h-12 bg-white/10" />
                <div className="text-center">
                  <p className="font-mono text-3xl font-bold text-white">20+</p>
                  <p className="text-zinc-500 text-sm">Years Experience</p>
                </div>
                <div className="w-px h-12 bg-white/10" />
                <div className="text-center">
                  <p className="font-mono text-3xl font-bold text-amber-400">KC</p>
                  <p className="text-zinc-500 text-sm">Born & Based</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0C0C0E]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight uppercase text-white mb-6">
            Ready to Join the Society?
          </h2>
          <p className="text-xl text-zinc-400 mb-10">
            Be part of a community that puts collectors first.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button className="btn-primary text-lg px-10" data-testid="about-join-btn">
                Create Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/marketplace">
              <Button variant="outline" className="btn-secondary text-lg px-10" data-testid="about-browse-btn">
                Browse Marketplace
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src={process.env.REACT_APP_LOGO_URL} 
              alt="Slab Society"
              className="w-8 h-8 rounded-lg object-contain"
            />
            <span className="text-zinc-500">© 2024 Slab Society. Founded by Ron Manthei</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/marketplace" className="hover:text-white transition-colors">Marketplace</Link>
            <Link to="/membership" className="hover:text-white transition-colors">Membership</Link>
            <Link to="/register" className="hover:text-white transition-colors">Sign Up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUsPage;
